// src/hr/portal/ProfileApprovals.js -- the profile's "Approvals" tab (mockup R3 setup card): who
// approves step 1, the fixed HR step 2, and who else is notified. Also-notify emails carry only the
// name and dates, never the type.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiCheck, FiInbox, FiX } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Avatar, Btn, Card, EmptyState, Field, Pill, Skeleton } from '../kit';
import { plainId } from '../kit/util';
import { daysLabel, shortRangeLabel } from '../format';
import { REQUEST_STATUS, typeTone } from '../timeoff/labels';
import { fullName, isCurrent } from './portalUtils';

const NO_APPROVER = 'No approver — requests go straight to HR';

// This person's requests in the benefit year, newest first: where each one stands. No notes.
function TheirRequests({ requests, types }) {
  const byName = new Map((types || []).map((t) => [t.name, t]));
  return (
    <Card delay={2} as="section" aria-label="Their requests" title="Their requests this year" className="hrp-pfl"
      action={<Link to="../requests">Requests <FiArrowRight aria-hidden="true" /></Link>}
    >
      {requests.length === 0 ? (
        <EmptyState compact icon={FiInbox} title="No requests yet">Requests they send from Time Off show up here.</EmptyState>
      ) : (
        <ul className="hrp-pfl__list">
          {requests.map((r) => {
            const status = REQUEST_STATUS[r.status] || { label: r.status, tone: 'mute' };
            return (
              <li key={r.id} className="hrp-pfl__it hrp-pfl__it--rq">
                <span className="hrp-pfl__dt">{shortRangeLabel(r.startDate, r.endDate)}</span>
                {r.typeName && <Pill {...typeTone(byName.get(r.typeName) || { name: r.typeName })} dot>{r.typeName}</Pill>}
                <span className="hrp-pfl__len">{Number(r.totalHours) === 4 ? 'Half day' : daysLabel(r.totalHours)}</span>
                <Pill tone={status.tone}>{status.label}</Pill>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function ApprovalsCard({ data, everyone, types, onToast, onReload }) {
  const person = data.person;
  const byId = useMemo(() => new Map(everyone.map((p) => [p.id, p])), [everyone]);
  const choices = useMemo(() => everyone
    .filter((p) => isCurrent(p) && p.id !== person.id)
    .map((p) => ({ value: p.id, label: fullName(p) }))
    .sort((a, b) => a.label.localeCompare(b.label)), [everyone, person.id]);

  const savedApprover = person.approverId || '';
  const savedNotify = useMemo(() => person.notifyIds || [], [person.notifyIds]);
  const savedKey = `${savedApprover}|${savedNotify.join(',')}`;
  const [approverId, setApproverId] = useState(savedApprover);
  const [notifyIds, setNotifyIds] = useState(savedNotify);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ids = `hrp-setup-${plainId(useId())}`;

  // New saved values (after a save or reload) reset the card; the first render already has them.
  const shownKey = useRef(savedKey);
  useEffect(() => {
    if (shownKey.current === savedKey) return;
    shownKey.current = savedKey;
    const [a, n] = savedKey.split('|');
    setApproverId(a);
    setNotifyIds(n ? n.split(',') : []);
  }, [savedKey]);

  const approver = approverId ? byId.get(approverId) : null;
  const approverGone = !!approverId && (!approver || !isCurrent(approver));
  const approverOptions = [{ value: '', label: NO_APPROVER }, ...choices];
  if (approverGone) {
    approverOptions.push({ value: approverId, label: `${approver ? fullName(approver) : person.approverName || 'Former approver'} (no longer here)` });
  }
  const addable = choices.filter((c) => !notifyIds.includes(c.value));
  const dirty = approverId !== savedApprover || notifyIds.join(',') !== savedNotify.join(',');

  const save = async () => {
    if (!dirty || busy) return;
    setBusy(true);
    setError('');
    try {
      await hr.admin.people.update(person.id, { approverId: approverId || null, notifyIds });
      setBusy(false);
      onToast('Approvals saved');
      onReload();
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Couldn't save the approvals. Try again."));
    }
  };

  return (
    <div className="hrp-pf-cols hrp-pf-cols--appr">
      <Card feat delay={1} as="section" aria-label={`Approvals for ${fullName(person)}`} className="hrp-setup">
        <h2 className="hrp-card__title">{`Approvals · ${fullName(person)}`}</h2>

        <div className="hrp-setup__row">
          <Field
            label="Approver (step 1)"
            type="select"
            value={approverId}
            onChange={(e) => setApproverId(e.target.value)}
            options={approverOptions}
            hint={approverGone ? 'This approver is no longer here, so their requests fall to HR. Pick someone new.' : undefined}
          />
        </div>

        <div className="hrp-setup__row">
          <div className="hrp-field__label">Final sign-off (step 2)</div>
          <div className="hrp-setup__fixed">HR <span>always</span></div>
        </div>

        <div className="hrp-setup__row">
          <div className="hrp-field__label" id={`${ids}-notify`}>Also notify</div>
          <ul className="hrp-setup__tags" aria-labelledby={`${ids}-notify`}>
            {notifyIds.map((id) => {
              const p = byId.get(id);
              const name = p ? fullName(p) : 'Someone no longer listed';
              return (
                <li key={id} className="hrp-setup__tag">
                  <Avatar id={id} name={name} size="sm" />
                  <span>{name}</span>
                  <button type="button" className="hrp-iconbtn hrp-setup__x" aria-label={`Remove ${name}`} onClick={() => setNotifyIds((list) => list.filter((x) => x !== id))}>
                    <FiX aria-hidden="true" />
                  </button>
                </li>
              );
            })}
            {notifyIds.length === 0 && <li className="hrp-setup__none">Nobody else</li>}
          </ul>
          {addable.length > 0 && (
            <div className="hrp-setup__add">
              <label className="hrp-sr" htmlFor={`${ids}-add`}>Add someone to notify</label>
              <select
                id={`${ids}-add`}
                className="hrp-select hrp-setup__addsel"
                value=""
                onChange={(e) => { const v = e.target.value; if (v) setNotifyIds((list) => [...list, v]); }}
              >
                <option value="">+ Add someone to notify</option>
                {addable.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          )}
        </div>

        <p className="hrp-setup__hint">
          No approver set → requests go straight to HR. If the approver is also HR, one approval covers both steps.
          Sick days skip approval; the approver and HR get a heads-up. People on the also-notify list see the name and
          dates only — never the type.
        </p>

        {error && <div className="hrp-form-err" role="alert"><FiAlertCircle aria-hidden="true" /><span>{error}</span></div>}
        <div className="hrp-setup__foot">
          <Btn
            variant="ghost"
            size="sm"
            disabled={!dirty || busy}
            onClick={() => { setApproverId(savedApprover); setNotifyIds(savedNotify); setError(''); }}
          >
            Reset
          </Btn>
          <Btn size="sm" icon={FiCheck} loading={busy} disabled={!dirty} onClick={save}>Save approvals</Btn>
        </div>
      </Card>
      <TheirRequests requests={data.requests || []} types={types} />
    </div>
  );
}

// The pickers need the people list; until it arrives nobody can be told apart from someone who left.
export function ProfileApprovals({ data, people, types, onToast, onReload }) {
  const everyone = useMemo(() => (people.data && people.data.people) || null, [people.data]);
  if (!everyone) {
    return (
      <div className="hrp-pf-cols hrp-pf-cols--appr">
        <Card className="hrp-setup">
          {people.status === 'error' ? (
            <EmptyState compact icon={FiAlertCircle} title="Couldn't load people" action={<Btn variant="ghost" size="sm" onClick={people.reload}>Try again</Btn>}>
              The approver and notify lists need it.
            </EmptyState>
          ) : <Skeleton label="Loading people" lines={5} height={16} />}
        </Card>
      </div>
    );
  }
  return <ApprovalsCard data={data} everyone={everyone} types={types} onToast={onToast} onReload={onReload} />;
}

export default ProfileApprovals;
